import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useUrlFilters } from "/imports/libs/url";

/**
 * Routes the four outcomes of a Google press: a brand-new client still owes us a
 * phone number, an existing Apointa user has to join this club first, a fresh
 * profile collects preferences, and everyone else lands where they were heading.
 */
export default function useGoogleSignIn({ onError } = {}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { slug } = useWidget();
  const { googleSignIn } = useAuth();
  const { filters } = useUrlFilters({});
  const [pending, setPending] = useState(false);

  const handleCode = useCallback(
    async (code) => {
      setPending(true);
      try {
        const data = await googleSignIn(code, slug);
        const redirect = filters.redirect || "";

        if (data?.status === "needs_profile") {
          navigate(`/${slug}/google-profile`, { state: { profileToken: data.profileToken, prefill: data.prefill, redirect } });
          return;
        }

        if (data?.status === "needs_onboard") {
          navigate(`/${slug}/onboard${redirect ? `?redirect=${redirect}` : ""}`);
          return;
        }

        if (data?.preferencesToken) {
          navigate(`/${slug}/preferences`, { state: { preferencesToken: data.preferencesToken, afterSave: "home", redirect } });
          return;
        }

        enqueueSnackbar("Signed in successfully!", { variant: "success" });
        navigate(`/${slug}/${redirect}`);
      } catch (err) {
        onError?.(err.message || "Google sign-in failed. Please try again.");
      } finally {
        setPending(false);
      }
    },
    [googleSignIn, slug, filters.redirect, navigate, enqueueSnackbar, onError]
  );

  return { handleCode, pending };
}
